import React from 'react';
import { Anchor, Tooltip } from '@mantine/core';
import { useConsent } from './useConsent';
import { cookiePolicyHref, privacyNoticeHref } from './cookiePolicyRoute';
import { ensureReadable } from '../../shared/colors/colorContrast';

const EDITOR_HINT = 'Obligatoire sur le site publié — non supprimable';

/**
 * Les deux liens de consentement, à l'intérieur du pied de page du site.
 *
 * **Calculés, jamais stockés.** Ils ne figurent pas dans la prop `links` du
 * pied de page : s'ils y étaient, le client pourrait les supprimer, les
 * renommer, les réordonner, ou faire pointer « Politique cookies » ailleurs.
 * Ils sont rendus par le code, et le panneau de droite ne les voit pas.
 *
 * **Ils prennent le style des autres liens, avec un plancher.** La règle n'est
 * pas « qu'ils soient lisibles » mais « qu'ils ne soient jamais moins visibles
 * que les liens du client ». D'où le style hérité, plus une garde de contraste
 * contre le fond du pied de page : le client règle librement ses couleurs, et
 * peut très bien poser une couleur de lien proche de son fond.
 *
 * En disposition multi-colonnes, ils se rendent **séparément** — un par
 * colonne, voir `SiteFooter` — pour suivre le flux du pied de page plutôt que
 * de s'empiler au bout de la dernière colonne.
 */

function useConsentLinkStyle(linkStyle, backgroundColor) {
  return {
    ...linkStyle,
    color: ensureReadable(linkStyle?.color, backgroundColor),
  };
}

/**
 * « Gérer mes cookies » ouvre un panneau : c'est un `<button>`, pas un lien.
 * Or un bouton s'étire sur toute la largeur d'une colonne et centre son
 * libellé — il se retrouvait décalé au milieu, quand ses voisins sont alignés
 * à gauche. On lui rend le comportement d'un texte.
 */
const BUTTON_AS_LINK = {
  alignSelf: 'flex-start',
  textAlign: 'left',
  padding: 0,
  border: 0,
  background: 'none',
  cursor: 'pointer',
};

/** Hors site publié — édition, aperçu, revue — le lien se voit mais n'agit pas. */
function InertLink({ children, style, isEditing }) {
  const link = (
    <Anchor component="span" size="sm" style={style} aria-disabled="true">
      {children}
    </Anchor>
  );
  if (!isEditing) return link;
  return (
    <Tooltip label={EDITOR_HINT} position="top" withArrow multiline w={220}>
      {link}
    </Tooltip>
  );
}

/** La notice « Vos données » : qui traite les données du lecteur, et ses droits. */
export function ConsentNoticeLink({ linkStyle, backgroundColor, isEditing = false }) {
  const { enforced } = useConsent();
  const style = useConsentLinkStyle(linkStyle, backgroundColor);

  if (!enforced) {
    return <InertLink style={style} isEditing={isEditing}>Vos données</InertLink>;
  }
  return (
    <Anchor href={privacyNoticeHref()} size="sm" style={style}>
      Vos données
    </Anchor>
  );
}

export function ConsentPolicyLink({ linkStyle, backgroundColor, isEditing = false }) {
  const { enforced } = useConsent();
  const style = useConsentLinkStyle(linkStyle, backgroundColor);

  if (!enforced) {
    return <InertLink style={style} isEditing={isEditing}>Politique cookies</InertLink>;
  }
  return (
    <Anchor href={cookiePolicyHref()} size="sm" style={style}>
      Politique cookies
    </Anchor>
  );
}

export function ConsentManageLink({ linkStyle, backgroundColor, isEditing = false }) {
  const { enforced, canManage, showPreferences } = useConsent();
  const style = useConsentLinkStyle(linkStyle, backgroundColor);

  if (!enforced) {
    return <InertLink style={style} isEditing={isEditing}>Gérer mes cookies</InertLink>;
  }
  // Avant que la bibliothèque n'ait démarré, le panneau ne s'ouvrirait pas :
  // mieux vaut ne rien proposer qu'un réglage inerte sans le dire.
  if (!canManage) return null;
  return (
    <Anchor
      component="button"
      type="button"
      size="sm"
      style={{ ...style, ...BUTTON_AS_LINK }}
      onClick={showPreferences}
    >
      Gérer mes cookies
    </Anchor>
  );
}

/** Tous à la suite — dispositions en ligne (centered, minimal). */
export function ConsentFooterLinks(props) {
  return (
    <>
      <ConsentNoticeLink {...props} />
      <ConsentPolicyLink {...props} />
      <ConsentManageLink {...props} />
    </>
  );
}

export default ConsentFooterLinks;
